import React, { useEffect, useMemo, useRef } from 'react';
import { Resizable } from 're-resizable';
import { useAuthorization } from '@hooks/useAuthorization';
import useEffectUpdateOnly from '@hooks/useEffectUpdateOnly';
import usePipelineFeedback from '@hooks/usePipelineFeedback';
import useScrollToBottomOnChange from '@hooks/useScrollToBottomOnChange';
import { StatusCodes } from '@constants/enum/common';
import { PipelineStatusCodes } from '@constants/pipelines';
import {
  DESCRIPTION_OPTIONS,
  DESCRIPTION_SUBTITLE,
  DESCRIPTION_TITLE,
  MIN_PROMPT_EDITOR_HEIGHT,
} from '@constants/prompt-explorer';
import {
  IPipeline,
  IPromptNode,
  IPromptPlayground,
  ISearchResult,
  PermissionAction,
  PermissionAsset,
} from '@redux/types/types';
import FeatureDescription from '@components/featureDescription/FeatureDescription';
import ConversationalResultsContainer from '@components/search/organisms/results/ConversationalResultsContainer';
import SearchNoResultsContainer from '@components/search/organisms/results/SearchNoResultsContainer';
import { RenderPipelineActionsBar } from './PipelineActionsBar/PipelineActionsBar';
import { RenderPromptEditor } from './RenderPromptEditor/RenderPromptEditor';
import styles from './pipelinePlayground.module.scss';
import usePromptEditor from '../hooks/usePromptEditor';
import { UpdatePrompt } from '../PromptExplorer';

export enum PlaygroundMoreOption {
  NEW_SESSION = 'newSession',
  CLEAR_CHAT = 'clearChat',
}

export interface IPipelinePromptPlaygroundProps {
  displayPipelineActionsBar: boolean;
  playground: IPromptPlayground;
  totalPlaygrounds: number;
  pipeline: IPipeline | null;
  search: {
    results: ISearchResult[];
    status: StatusCodes;
    query: string;
    searching: boolean;
    displayTypingEffect?: boolean;
  };
  options: {
    displayRemovePipelineButton: boolean;
    renderDescription: boolean;
    displayHeader: boolean;
    displayExpandShrinkButton: boolean;
  };
  promptEditorContainerExpanded: boolean;
  promptEditorHeight: number;
  activeFilters: boolean;
  onPromptEditorValueChange: (_: UpdatePrompt) => void;
  onPromptEditorPromptNodeChange: ({
    node,
    playgroundId,
  }: {
    node: string;
    playgroundId: string;
  }) => void;
  onSelectPipeline: ({
    pipelineName,
    playgroundId,
  }: {
    pipelineName: string;
    playgroundId: string;
  }) => void;
  onAddNewPipeline: (playgroundId: string, pipelineName: string) => void;
  onRemovePipeline: (playgroundId: string) => void;
  onDisplayFilters: (playgroundId: string) => void;
  onStartNewSession: (playgroundId: string) => void;
  onClearChat: (playgroundId: string) => void;
  onExpandShrinkPromptEditorContainer: () => void;
  onTemplatesLinkClick: (playgroundId: string) => void;
  onRepeatQueryOptionClick: (query: string) => void;
  onSaveQueryPromptTemplateOptionClick: (playgroundId: string, prompt: string) => void;
  onPromptEditorResize: (height: number) => void;
  onUpdatePipelinePrompt: (playgroundId: string) => void;
  onActivatePipeline: (playgroundId: string) => void;
  initialPrompts: string[];
}

interface Params {
  [key: string]: { template: string } & Record<string, unknown>;
}

const PipelinePlayground = ({
  displayPipelineActionsBar,
  playground,
  totalPlaygrounds,
  pipeline,
  search,
  options,
  promptEditorContainerExpanded,
  promptEditorHeight,
  activeFilters,
  onExpandShrinkPromptEditorContainer,
  onPromptEditorValueChange,
  onPromptEditorPromptNodeChange,
  onSelectPipeline,
  onAddNewPipeline,
  onRemovePipeline,
  onDisplayFilters,
  onStartNewSession,
  onClearChat,
  onTemplatesLinkClick,
  onRepeatQueryOptionClick,
  onSaveQueryPromptTemplateOptionClick,
  onPromptEditorResize,
  onUpdatePipelinePrompt,
  onActivatePipeline,
  initialPrompts,
}: IPipelinePromptPlaygroundProps) => {
  const { pipelineName, id: playgroundId, prompts, selectedPromptNode } = playground;

  const {
    results: searchResults,
    status: searchStatus,
    query: searchQuery,
    searching,
    displayTypingEffect,
  } = search;
  const {
    displayRemovePipelineButton,
    renderDescription,
    displayHeader,
    displayExpandShrinkButton,
  } = options;
  const isSearching = searchStatus === StatusCodes.IN_PROGRESS;
  const isPipelineIdle =
    pipeline?.status === PipelineStatusCodes.IDLE ||
    pipeline?.status === PipelineStatusCodes.ACTIVATING;

  const searchResultsWrapperRef = useRef<HTMLDivElement>(null);
  useScrollToBottomOnChange(searchResultsWrapperRef, [searchResults, searching]);

  const { hasPermission } = useAuthorization();
  const hasReadFilesPermission = hasPermission({
    [PermissionAsset.FILES]: PermissionAction.READ,
  });
  const hasWriteFeedbackPermission = hasPermission({
    [PermissionAsset.FEEDBACK]: PermissionAction.WRITE,
  });

  useEffectUpdateOnly(() => {
    if (!searchResults?.length) return;
    const lastResult = searchResults[searchResults.length - 1];

    // TODO: Handle it inside useScrollToBottomOnChange hook
    // Currently it doesn't detect ref mutation
    if (lastResult.infoMessage) {
      searchResultsWrapperRef.current?.scrollTo({
        top: searchResultsWrapperRef.current?.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [searchResults]);

  const editorPrompts = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(prompts).filter((data) => initialPrompts.includes(data[0])),
      ) as Record<string, IPromptNode>,
    [prompts, initialPrompts],
  );

  // Prompt editor
  const {
    modalContextHolder,
    isUpdatePipelineButtonDisabled,
    getUpdatePipelinePromptTooltipText,
    onUpdatePipelinePromptClick,
  } = usePromptEditor({
    pipeline,
    prompts: editorPrompts,
    pipelineName,
    selectedPromptNode,
    onUpdatePipelinePrompt: () => onUpdatePipelinePrompt(playgroundId),
  });

  // Feedback tags
  const { getFeedbackTags, resetFeedbackData } = usePipelineFeedback();

  const mappedPrompts = useMemo(() => {
    if (!initialPrompts.length) return {};

    return Object.fromEntries(
      Object.entries(prompts).map(([k, v]) => {
        if (initialPrompts.includes(k)) {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          const { template, template_name, ...rest } = v as IPromptNode;
          void template_name;
          return [k, { template, ...rest }];
        }
        return [k, v];
      }),
    ) as Params;
  }, [prompts, initialPrompts]);

  const onParamsChange = React.useCallback(
    (value: Record<string, unknown>) => {
      onPromptEditorValueChange({ playgroundId, update: value });
    },
    [prompts, initialPrompts],
  );

  useEffect(() => {
    resetFeedbackData();
  }, []);

  useEffect(() => {
    if (!pipeline?.pipeline_id) return;

    getFeedbackTags({ pipelineId: pipeline.pipeline_id });
  }, [pipeline?.pipeline_id]);

  const handleMoreOptionsDropdownMenuItemClick = ({ key }: { key: string }) => {
    if (key === PlaygroundMoreOption.NEW_SESSION) onStartNewSession(playgroundId);
    if (key === PlaygroundMoreOption.CLEAR_CHAT) onClearChat(playgroundId);
  };

  const renderEmptyState = () => {
    if (isPipelineIdle)
      return (
        <SearchNoResultsContainer
          selectedPipeline={pipeline}
          status={searchStatus}
          query={searchQuery}
          onActivatePipeline={() => onActivatePipeline(playgroundId)}
        />
      );

    if (!renderDescription) return null;

    return (
      <div className={styles.descriptionMessage}>
        <FeatureDescription
          title={DESCRIPTION_TITLE}
          subtitle={DESCRIPTION_SUBTITLE}
          options={DESCRIPTION_OPTIONS}
        />
      </div>
    );
  };

  const prompt = mappedPrompts[selectedPromptNode];
  return (
    <div className={styles.pipelinePlayground}>
      {modalContextHolder}

      {displayPipelineActionsBar && (
        <div className={styles.pipelinePlayground_pipelineActionsBar_wrapper}>
          <RenderPipelineActionsBar
            handleMoreOptionsDropdownMenuItemClick={handleMoreOptionsDropdownMenuItemClick}
            totalPlaygrounds={totalPlaygrounds}
            pipelineName={pipelineName}
            pipeline={pipeline}
            displayRemovePipelineButton={displayRemovePipelineButton}
            onSelectPipeline={onSelectPipeline}
            isSearching={isSearching}
            playgroundId={playgroundId}
            mappedPrompts={mappedPrompts}
            onParamsChange={onParamsChange}
            onDisplayFilters={onDisplayFilters}
            onRemovePipeline={onRemovePipeline}
            onAddNewPipeline={onAddNewPipeline}
            activeFilters={activeFilters}
            initialPrompts={initialPrompts}
          />
        </div>
      )}
      <div
        className={`${styles.pipelinePlayground_searchResults_wrapper} ${
          promptEditorContainerExpanded
            ? styles.pipelinePlayground_searchResults_wrapper_expanded
            : ''
        }`}
        ref={searchResultsWrapperRef}
      >
        <div className={styles.zeroHeightContainer}>
          <div className={styles.pipelinePlayground_searchResults_paddingContainer}>
            {pipelineName && (searchResults?.length > 0 || searching) ? (
              <ConversationalResultsContainer
                pipelineId={pipeline?.pipeline_id || ''}
                pipelineName={pipeline?.name || ''}
                results={searchResults}
                displayFileOptions={hasReadFilesPermission}
                displayMoreOptions
                displayFeedbackOptions={hasWriteFeedbackPermission}
                displayTypingEffect={displayTypingEffect}
                status={searchStatus}
                query={searchQuery}
                onRepeatQueryOptionClick={onRepeatQueryOptionClick}
                onSaveQueryPromptTemplateOptionClick={(template) =>
                  onSaveQueryPromptTemplateOptionClick(playgroundId, template)
                }
              />
            ) : (
              renderEmptyState()
            )}
          </div>
        </div>
      </div>

      <Resizable
        size={{ width: 'auto', height: promptEditorHeight }}
        minWidth="auto"
        minHeight={MIN_PROMPT_EDITOR_HEIGHT}
        maxHeight={promptEditorContainerExpanded ? '100%' : 600}
        grid={[10, 10]}
        snapGap={10}
        enable={{
          top: true,
          right: false,
          bottom: false,
          left: false,
          topRight: false,
          bottomRight: false,
          bottomLeft: false,
          topLeft: false,
        }}
        onResizeStop={(e, direction, ref, d) => {
          onPromptEditorResize(d.height);
        }}
        className={`${styles.pipelinePlayground_promptEditor_wrapper} ${
          promptEditorContainerExpanded
            ? styles.pipelinePlayground_promptEditor_wrapper_expanded
            : ''
        }`}
        handleClasses={{
          top: 'handle_top',
        }}
      >
        <RenderPromptEditor
          displayExpandShrinkButton={displayExpandShrinkButton}
          displayHeader={displayHeader}
          promptEditorContainerExpanded={promptEditorContainerExpanded}
          onExpandShrinkPromptEditorContainer={onExpandShrinkPromptEditorContainer}
          selectedPromptNode={selectedPromptNode}
          pipelineParamsTemplate={prompt?.template ?? ''}
          getUpdatePipelinePromptTooltipText={getUpdatePipelinePromptTooltipText}
          isUpdatePipelineButtonDisabled={isUpdatePipelineButtonDisabled}
          onUpdatePipelinePromptClick={onUpdatePipelinePromptClick}
          onPromptEditorPromptNodeChange={onPromptEditorPromptNodeChange}
          onPromptEditorValueChange={onPromptEditorValueChange}
          playgroundId={playgroundId}
          onTemplatesLinkClick={onTemplatesLinkClick}
          prompts={editorPrompts}
        />
      </Resizable>
    </div>
  );
};

export default PipelinePlayground;
